import * as Schema from "effect/Schema";
import { Dimensions, Identifier, NonEmptyText, Rect, TimeMs, ValidationError } from "@linempo/kernel";
import { AnnotationProject, AnnotationScene, AssetDescriptor, ErasePolicy, FillRegion, ManualStroke, ProtectionRegion, SubtitleTrack, annotationScenes, decodeProject, subtitleCues, AnimationDefinition, DrawingDuration, HandStyle, SceneReference, COMPLETED_HOLD_MS, MAX_PROJECT_DURATION_MS, allocateAnimationId, animationForAsset, animationTimeScale, defaultAnimation, definitionFromScene, referenceFromScene, sceneReferences, } from "./model.js";
/** Commands contain metadata only. Public IDs are supplied by the caller; animation IDs derive deterministically. */
export const EditCommand = Schema.Union([
    Schema.TaggedStruct("RenameProject", { name: NonEmptyText }),
    Schema.TaggedStruct("AddAssets", { assets: Schema.Array(AssetDescriptor).check(Schema.isMinLength(1)) }),
    Schema.TaggedStruct("RemoveAsset", { assetId: Identifier }),
    Schema.TaggedStruct("InsertScene", { trackId: Identifier, scene: AnnotationScene }),
    Schema.TaggedStruct("MapAssetToCues", {
        trackId: Identifier, sceneId: Identifier, assetId: Identifier,
        cueIds: Schema.Array(Identifier).check(Schema.isMinLength(1)), placement: Schema.optionalKey(Rect),
    }),
    Schema.TaggedStruct("InsertAssetAt", {
        trackId: Identifier, sceneId: Identifier, assetId: Identifier, startMs: TimeMs,
        durationMs: Schema.optionalKey(TimeMs.check(Schema.isGreaterThan(0))), placement: Schema.optionalKey(Rect),
    }),
    Schema.TaggedStruct("MoveScene", { sceneId: Identifier, startMs: TimeMs }),
    Schema.TaggedStruct("TrimScene", { sceneId: Identifier, startMs: TimeMs, endMs: TimeMs, resolution: Schema.optionalKey(Schema.Literals(["fit", "extend"])) }),
    Schema.TaggedStruct("PlaceScene", { sceneId: Identifier, placement: Rect }),
    Schema.TaggedStruct("ReplaceSceneAsset", { sceneId: Identifier, assetId: Identifier, resetAnnotations: Schema.Boolean }),
    Schema.TaggedStruct("DeleteScene", { sceneId: Identifier }),
    Schema.TaggedStruct("ReturnSceneToLibrary", { sceneId: Identifier }),
    Schema.TaggedStruct("DeleteSceneFromProject", { sceneId: Identifier }),
    Schema.TaggedStruct("DeleteSubtitleCues", { cueIds: Schema.Array(Identifier).check(Schema.isMinLength(1)) }),
    Schema.TaggedStruct("SetHandStyle", { handStyle: HandStyle }),
    Schema.TaggedStruct("SetDrawingDuration", { animationId: Identifier, drawingDuration: DrawingDuration,
        resolution: Schema.optionalKey(Schema.Literals(["fit", "extend"])) }),
    Schema.TaggedStruct("UpdateAnimationAnnotations", { animationId: Identifier, drawMode: Schema.Literals(["automatic", "manual"]),
        manualStrokes: Schema.Array(ManualStroke), fillRegions: Schema.Array(FillRegion), protectionRegions: Schema.Array(ProtectionRegion) }),
    Schema.TaggedStruct("UpdateSceneAnnotations", {
        sceneId: Identifier, drawMode: Schema.Literals(["automatic", "manual"]),
        manualStrokes: Schema.Array(ManualStroke), fillRegions: Schema.Array(FillRegion),
        protectionRegions: Schema.Array(ProtectionRegion), erase: Schema.optionalKey(ErasePolicy),
    }),
    Schema.TaggedStruct("ImportSubtitles", { mode: Schema.Literals(["replace", "append"]), track: SubtitleTrack }),
    Schema.TaggedStruct("RestoreContent", { project: AnnotationProject }),
]);
export const EditTransaction = Schema.Struct({
    commands: Schema.Array(EditCommand).check(Schema.isMinLength(1)),
    label: Schema.optionalKey(NonEmptyText),
});
const invalid = (reason, message) => { throw new ValidationError({ path: `edit.${reason}`, message }); };
const failure = (error) => error instanceof ValidationError ? error : new ValidationError({
    path: "edit.validation", message: error instanceof Error ? error.message : String(error),
});
export const makeProject = (id, name = "未命名工程") => decodeProject({
    schemaVersion: 3, sceneMode: "clips", id, name, revision: 0, animations: [], settings: { handStyle: "pen" },
    canvas: { width: 1920, height: 1080 }, durationMs: 0, assets: [], tracks: [],
});
export function fitPlacement(asset, canvas) {
    const scale = Math.min(canvas.width * 0.78 / asset.dimensions.width, canvas.height * 0.72 / asset.dimensions.height);
    const width = asset.dimensions.width * scale;
    const height = asset.dimensions.height * scale;
    return { x: (canvas.width - width) / 2, y: Math.max(0, (canvas.height * 0.84 - height) / 2), width, height };
}
/** Continuity means adjacent source entries in ONE subtitle track, independent of timestamps. */
export function subtitleSelectionRange(project, cueIds) {
    if (!cueIds.length || new Set(cueIds).size !== cueIds.length)
        return invalid("selection", "请选择同一字幕轨中连续且不重复的字幕。");
    const selected = new Set(cueIds);
    const track = project.tracks.find((item) => item._tag === "subtitles" && cueIds.every((id) => item.cues.some((cue) => cue.id === id)));
    if (!track || track._tag !== "subtitles")
        return invalid("selection", "字幕选区必须属于同一字幕轨。");
    const indexes = track.cues.flatMap((cue, index) => selected.has(cue.id) ? [index] : []);
    if (indexes.at(-1) - indexes[0] + 1 !== indexes.length)
        return invalid("selection", "请选择连续字幕，不能跳过中间条目。");
    const cues = track.cues.filter((cue) => selected.has(cue.id));
    return { startMs: Math.min(...cues.map((cue) => cue.startMs)), endMs: Math.max(...cues.map((cue) => cue.endMs)) };
}
/** Move and trim preserve relative annotation timing; continuous erase retains its trailing gap. */
export function retimeScene(scene, startMs, endMs) {
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || startMs < 0 || endMs <= startMs)
        return invalid("time", "镜头须满足 0 ≤ 开始 < 结束。");
    const scale = (endMs - startMs) / (scene.endMs - scene.startMs);
    const retime = (item) => ({ ...item,
        startMs: Math.max(startMs, Math.min(endMs, startMs + (item.startMs - scene.startMs) * scale)),
        endMs: Math.max(startMs, Math.min(endMs, startMs + (item.endMs - scene.startMs) * scale)),
    });
    return { ...scene, startMs, endMs, manualStrokes: scene.manualStrokes.map(retime), fillRegions: scene.fillRegions.map(retime),
        erase: scene.erase._tag === "step" ? { _tag: "step", atMs: endMs + scene.erase.atMs - scene.endMs } : scene.erase,
    };
}
const overlappingCueIds = (project, range) => subtitleCues(project)
    .filter((cue) => cue.startMs < range.endMs && cue.endMs > range.startMs).map((cue) => cue.id);
const sceneEnd = (project, scene) => project.sceneMode === "continuous" && scene.erase._tag === "step" ? scene.erase.atMs : scene.endMs;
const updateDuration = (project) => ({ ...project, durationMs: Math.max(project.sceneMode === "clips" ? 0 : project.durationMs, ...subtitleCues(project).map((cue) => cue.endMs), ...annotationScenes(project).map((scene) => sceneEnd(project, scene))),
});
const getAsset = (project, id) => project.assets.find((asset) => asset.id === id) ?? invalid("asset", "素材不存在。");
const getScene = (project, id) => sceneReferences(project).find((scene) => scene.id === id) ?? invalid("scene", "镜头不存在。");
const getAnimation = (project, id) => project.animations.find((animation) => animation.id === id) ?? invalid("animation", "动画不存在。");
function updateAnimation(project, animation) {
    return { ...project, animations: project.animations.map((existing) => existing.id === animation.id ? animation : existing) };
}
function updateScene(project, id, update) {
    getScene(project, id);
    return { ...project, tracks: project.tracks.map((track) => track._tag !== "scenes" ? track : {
            ...track, scenes: track.scenes.map((scene) => scene.id === id ? update(scene) : scene),
        }) };
}
function assertUnused(project, assetId, exceptSceneId) {
    const asset = getAsset(project, assetId);
    if (annotationScenes(project).some((scene) => scene.id !== exceptSceneId && getAsset(project, scene.assetId).hash === asset.hash)) {
        return invalid("duplicate-placement", "这张图片已在时间轴上，请定位已有镜头。");
    }
}
function insertReference(project, trackId, scene) {
    if (sceneReferences(project).some((existing) => existing.id === scene.id))
        return invalid("scene", "镜头 ID 已存在。");
    assertUnused(project, getAnimation(project, scene.animationId).assetId);
    const track = project.tracks.find((item) => item.id === trackId);
    if (track && track._tag !== "scenes")
        return invalid("track", "目标轨道不是镜头轨道。");
    const inserted = { ...project, tracks: track ? project.tracks.map((item) => item.id === trackId && item._tag === "scenes"
            ? { ...item, scenes: [...item.scenes, scene] } : item) : [...project.tracks, { _tag: "scenes", id: trackId, scenes: [scene] }] };
    return reconcileDrawingDuration(inserted, scene.animationId);
}
/** Compatibility import of a resolved scene. An explicit definition reference remains authoritative. */
function insertScene(project, trackId, scene) {
    assertUnused(project, scene.assetId);
    if (scene.animationId) {
        const animation = getAnimation(project, scene.animationId);
        if (animation.assetId !== scene.assetId)
            return invalid("animation", "动画与素材不匹配。");
        return insertReference(project, trackId, referenceFromScene(scene, animation.id));
    }
    const existing = animationForAsset(project, scene.assetId);
    const pristine = existing.fitTiming === "completed-frame" && existing.drawMode === "automatic"
        && existing.manualStrokes.length === 0 && existing.fillRegions.length === 0 && existing.protectionRegions.length === 0
        && existing.drawingDuration._tag === "fit" && existing.localDurationMs === 3000 && existing.lastSceneDurationMs === 3000;
    // Only a pristine default may be hydrated by the old absolute-scene authoring API.
    const animation = pristine ? definitionFromScene(scene, existing.id) : existing;
    return insertReference(updateAnimation(project, animation), trackId, referenceFromScene(scene, animation.id));
}
function newScene(project, id, assetId, range, cueIds, placement) {
    const asset = getAsset(project, assetId);
    const animation = animationForAsset(project, assetId);
    return { id, animationId: animation.id, ...range, cueIds, placement: placement ?? fitPlacement(asset, project.canvas), erase: { _tag: "none" } };
}
function returnScene(project, sceneId) {
    const scene = getScene(project, sceneId);
    const animation = { ...getAnimation(project, scene.animationId), lastSceneDurationMs: scene.endMs - scene.startMs };
    // First definition per asset is its library selection; returning a legacy duplicate makes that definition primary.
    return { ...project, animations: [animation, ...project.animations.filter((item) => item.id !== animation.id)],
        tracks: project.tracks.map((track) => track._tag !== "scenes" ? track : { ...track, scenes: track.scenes.filter((item) => item.id !== sceneId) }) };
}
function removeAsset(project, assetId) {
    getAsset(project, assetId);
    const ids = new Set(project.animations.filter((animation) => animation.assetId === assetId).map((animation) => animation.id));
    return { ...project, assets: project.assets.filter((asset) => asset.id !== assetId),
        animations: project.animations.filter((animation) => animation.assetId !== assetId),
        tracks: project.tracks.map((track) => track._tag !== "scenes" ? track : { ...track, scenes: track.scenes.filter((scene) => !ids.has(scene.animationId)) }) };
}
function reconcileDrawingDuration(project, animationId, resolution) {
    const animation = getAnimation(project, animationId);
    if (animation.drawingDuration._tag !== "custom")
        return project;
    const required = animation.drawingDuration.durationMs + COMPLETED_HOLD_MS;
    const short = sceneReferences(project).filter((scene) => scene.animationId === animationId && scene.endMs - scene.startMs + 1e-7 < required);
    if (!short.length)
        return project;
    if (resolution === "fit")
        return updateAnimation(project, { ...animation, drawingDuration: { _tag: "fit" } });
    if (resolution !== "extend")
        return invalid("drawing-duration", "绘制用时需保留至少一帧完成画面，请适应镜头或延长至空白时段。");
    let next = project;
    for (const scene of short) {
        const endMs = scene.startMs + required;
        if (endMs + (project.sceneMode === "continuous" && scene.erase._tag === "step" ? scene.erase.atMs - scene.endMs : 0) > MAX_PROJECT_DURATION_MS || sceneReferences(next).some((other) => other.id !== scene.id && other.startMs < endMs && other.endMs > scene.endMs)) {
            return invalid("drawing-extension", "后方空白不足或超过十分钟，无法延长镜头。");
        }
        next = updateScene(next, scene.id, (current) => ({ ...current, endMs,
            cueIds: overlappingCueIds(next, { startMs: current.startMs, endMs }),
            erase: current.erase._tag === "step" ? { _tag: "step", atMs: endMs + current.erase.atMs - current.endMs } : current.erase }));
    }
    return next;
}
function retimeReference(project, sceneId, startMs, endMs, resolution) {
    if (endMs <= startMs)
        return invalid("time", "镜头须满足 0 ≤ 开始 < 结束。");
    const scene = getScene(project, sceneId);
    let next = updateScene(project, sceneId, (current) => ({ ...current, startMs, endMs,
        cueIds: overlappingCueIds(project, { startMs, endMs }),
        erase: current.erase._tag === "step" ? { _tag: "step", atMs: endMs + current.erase.atMs - current.endMs } : current.erase }));
    if (resolution === "fit")
        next = updateAnimation(next, { ...getAnimation(next, scene.animationId), drawingDuration: { _tag: "fit" } });
    return reconcileDrawingDuration(next, scene.animationId, resolution);
}
function authoredAnnotationsChanged(animation, incoming) {
    const stableTime = (time) => Math.round(time * 1e7) / 1e7;
    const content = (value) => JSON.stringify({
        drawMode: value.drawMode,
        manualStrokes: value.manualStrokes.map(({ id: _id, allowedProtectionIds: _allowed, ...stroke }) => ({ ...stroke, startMs: stableTime(stroke.startMs), endMs: stableTime(stroke.endMs) })),
        fillRegions: value.fillRegions.map(({ id: _id, allowedProtectionIds: _allowed, ...fill }) => ({ ...fill, startMs: stableTime(fill.startMs), endMs: stableTime(fill.endMs) })),
    });
    return content(animation) !== content(incoming);
}
function applyCommand(project, command) {
    switch (command._tag) {
        case "RenameProject": return { ...project, name: command.name };
        case "AddAssets": {
            let next = project;
            for (const asset of command.assets) {
                const collision = next.assets.find((item) => item.id === asset.id);
                if (collision && collision.hash !== asset.hash)
                    return invalid("asset-id", "素材 ID 已被其他图片使用。");
                if (next.assets.some((item) => item.hash === asset.hash))
                    continue;
                next = { ...next, assets: [...next.assets, asset], animations: [...next.animations, defaultAnimation(asset.id, allocateAnimationId(`animation-${asset.id}`, next.animations.map((animation) => animation.id)))] };
            }
            return next;
        }
        case "RemoveAsset": return removeAsset(project, command.assetId);
        case "InsertScene": return insertScene(project, command.trackId, command.scene);
        case "MapAssetToCues": {
            const range = subtitleSelectionRange(project, command.cueIds);
            const selected = new Set(command.cueIds);
            const cueIds = subtitleCues(project).filter((cue) => selected.has(cue.id)).map((cue) => cue.id);
            return insertReference(project, command.trackId, newScene(project, command.sceneId, command.assetId, range, cueIds, command.placement));
        }
        case "InsertAssetAt": {
            getAsset(project, command.assetId);
            const range = { startMs: command.startMs, endMs: command.startMs + (command.durationMs ?? animationForAsset(project, command.assetId).lastSceneDurationMs) };
            return insertReference(project, command.trackId, newScene(project, command.sceneId, command.assetId, range, overlappingCueIds(project, range), command.placement));
        }
        case "MoveScene": {
            const scene = getScene(project, command.sceneId);
            return retimeReference(project, scene.id, command.startMs, command.startMs + scene.endMs - scene.startMs);
        }
        case "TrimScene": return retimeReference(project, command.sceneId, command.startMs, command.endMs, command.resolution);
        case "PlaceScene": return updateScene(project, command.sceneId, (scene) => ({ ...scene, placement: command.placement }));
        case "ReplaceSceneAsset": {
            const scene = getScene(project, command.sceneId);
            const outgoing = getAnimation(project, scene.animationId);
            if (outgoing.assetId === command.assetId)
                return project;
            assertUnused(project, command.assetId, command.sceneId);
            // resetAnnotations is retained for wire compatibility; replacing switches definitions without erasing either.
            const target = animationForAsset(project, command.assetId);
            const retained = updateAnimation(project, { ...outgoing, lastSceneDurationMs: scene.endMs - scene.startMs });
            return reconcileDrawingDuration(updateScene(retained, scene.id, (current) => ({ ...current, animationId: target.id })), target.id);
        }
        case "DeleteScene":
        case "ReturnSceneToLibrary": return returnScene(project, command.sceneId);
        case "DeleteSceneFromProject": return removeAsset(project, getAnimation(project, getScene(project, command.sceneId).animationId).assetId);
        case "DeleteSubtitleCues": {
            const ids = new Set(command.cueIds);
            if (command.cueIds.some((id) => !subtitleCues(project).some((cue) => cue.id === id)))
                return invalid("cue", "字幕不存在。");
            return { ...project, tracks: project.tracks.map((track) => track._tag === "subtitles" ? { ...track, cues: track.cues.filter((cue) => !ids.has(cue.id)) }
                    : { ...track, scenes: track.scenes.map((scene) => ({ ...scene, cueIds: scene.cueIds.filter((id) => !ids.has(id)) })) }) };
        }
        case "SetHandStyle": return { ...project, settings: { ...project.settings, handStyle: command.handStyle } };
        case "SetDrawingDuration": {
            const animation = getAnimation(project, command.animationId);
            const drawingDuration = command.resolution === "fit" ? { _tag: "fit" } : command.drawingDuration;
            if (drawingDuration._tag === "custom" && drawingDuration.durationMs + COMPLETED_HOLD_MS > MAX_PROJECT_DURATION_MS)
                return invalid("drawing-duration", "绘制用时超过十分钟。");
            return reconcileDrawingDuration(updateAnimation(project, { ...animation, drawingDuration }), animation.id, command.resolution);
        }
        case "UpdateAnimationAnnotations": {
            const animation = getAnimation(project, command.animationId);
            return updateAnimation(project, { ...animation,
                ...(authoredAnnotationsChanged(animation, command) ? { fitTiming: "completed-frame" } : {}),
                drawMode: command.drawMode, manualStrokes: command.manualStrokes,
                fillRegions: command.fillRegions, protectionRegions: command.protectionRegions });
        }
        case "UpdateSceneAnnotations": {
            const scene = getScene(project, command.sceneId);
            const animation = getAnimation(project, scene.animationId);
            Schema.decodeUnknownSync(AnnotationScene)({ ...annotationScenes(project).find((item) => item.id === scene.id),
                drawMode: command.drawMode, manualStrokes: command.manualStrokes, fillRegions: command.fillRegions,
                protectionRegions: command.protectionRegions, erase: command.erase ?? scene.erase });
            const scale = animationTimeScale(animation, scene.endMs - scene.startMs);
            const local = (item) => ({ ...item,
                startMs: (item.startMs - scene.startMs) / scale, endMs: (item.endMs - scene.startMs) / scale });
            let manualStrokes = command.manualStrokes.map(local);
            let fillRegions = command.fillRegions.map(local);
            // Old absolute authoring callers may submit an operation ending at clipEnd. Normalize that schedule
            // into the local basis once, then the completed-frame fit policy supplies the hold without clipping it.
            const localEnd = Math.max(0, ...manualStrokes.map((stroke) => stroke.endMs), ...fillRegions.map((fill) => fill.endMs));
            if (animation.drawingDuration._tag === "fit" && localEnd > animation.localDurationMs) {
                const factor = animation.localDurationMs / localEnd;
                const normalize = (item) => ({ ...item, startMs: item.startMs * factor, endMs: item.endMs * factor });
                manualStrokes = manualStrokes.map(normalize);
                fillRegions = fillRegions.map(normalize);
            }
            const authored = { drawMode: command.drawMode, manualStrokes, fillRegions };
            const changed = updateAnimation(project, { ...animation, ...authored,
                ...(authoredAnnotationsChanged(animation, authored) ? { fitTiming: "completed-frame" } : {}),
                protectionRegions: command.protectionRegions });
            return command.erase ? updateScene(changed, scene.id, (current) => ({ ...current, erase: command.erase })) : changed;
        }
        case "ImportSubtitles": {
            const hash = command.track.metadata?.sourceHash;
            if (hash && project.tracks.some((track) => track._tag === "subtitles" && track.metadata?.sourceHash === hash)) {
                return invalid("duplicate-subtitles", "这份字幕已经导入。");
            }
            const tracks = command.mode === "replace" ? project.tracks.filter((track) => track._tag !== "subtitles") : project.tracks;
            const replaced = { ...project, tracks: [...tracks, command.track] };
            return { ...replaced, tracks: replaced.tracks.map((track) => track._tag !== "scenes" ? track : {
                    ...track, scenes: track.scenes.map((scene) => ({ ...scene, cueIds: overlappingCueIds(replaced, scene) })),
                }) };
        }
        case "RestoreContent": {
            if (command.project.id !== project.id)
                return invalid("identity", "撤销记录不属于当前工程。");
            return { ...command.project, id: project.id, revision: project.revision };
        }
    }
}
/** An atomic metadata transaction: no mutation, no IO, one new revision, validation before publication. */
export function applyEdit(project, input) {
    try {
        const current = decodeProject(project);
        const transaction = Schema.decodeUnknownSync(EditTransaction)("commands" in input ? input : { commands: [input] });
        let next = current;
        for (const command of transaction.commands)
            next = applyCommand(next, command);
        // Clips derive duration from remaining content; continuous keeps its explicit legacy tail.
        // History restoration always retains the snapshot duration exactly.
        if (transaction.commands.at(-1)?._tag !== "RestoreContent")
            next = updateDuration(next);
        if (next.sceneMode === "clips") {
            const ordered = [...annotationScenes(next)].sort((a, b) => a.startMs - b.startMs);
            if (ordered.some((scene, index) => index > 0 && scene.startMs < ordered[index - 1].endMs))
                return invalid("overlap", "镜头发生重叠，请选择空白时段或替换已有镜头。");
        }
        return decodeProject({ ...next, schemaVersion: 3, revision: current.revision + 1 });
    }
    catch (error) {
        throw failure(error);
    }
}
